import { useTranslation } from "react-i18next";
import { formatOpeningHoursDisplay } from "@/lib/dates.util";
import { GetAllCompanyDto, GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { OpeningHours } from "@/types/forms/auth/registerFormData.form";
import { InfoAccordionRow, InfoRowNested } from "./infoCard";
import { formatDate, getFullName } from "./expeditionDetail.utils";

/**
 * Pickup date and location; details show the registered pickup location when one was selected,
 * otherwise the pickup address typed by hand.
 */
export const PickupLocationAccordion = ({
  expedition,
  company,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company?: GetAllCompanyDto;
}) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const pickupInfo = expedition.pickupInfo;
  const pickupLocation = pickupInfo?.pickupLocation;
  const pickupDate = formatDate(pickupInfo?.pickupDate);
  const openingHours = company?.pickUpLocations?.find((pl) => pl.id === pickupLocation?.id)
    ?.openingHours as OpeningHours | undefined;

  const otherFullName = getFullName(pickupInfo?.pickupFirstName, pickupInfo?.pickupLastName);
  const hasOtherPickupInfo =
    pickupInfo?.pickupCompany ||
    otherFullName ||
    pickupInfo?.pickupStreet ||
    pickupInfo?.pickupPostalCode ||
    pickupInfo?.pickupCity ||
    pickupInfo?.pickupCountry ||
    pickupInfo?.pickupPhone;

  return (
    <InfoAccordionRow
      label={t("expeditionDrawerDetails.pickup")}
      value={
        <div className="flex flex-col items-end">
          <span>{pickupDate ?? "-"}</span>
          <span className="text-xs font-medium text-gray-500">
            {pickupLocation?.name ?? t("expeditionDrawerDetails.pickupLocationOther")}
          </span>
        </div>
      }
    >
      {pickupLocation ? (
        <>
          <InfoRowNested label={t("expeditionDrawerDetails.name")} value={pickupLocation?.name} />
          <InfoRowNested
            label={t("expeditionDrawerDetails.address")}
            value={pickupLocation.street}
          />
          <InfoRowNested
            label={t("expeditionDrawerDetails.city")}
            value={[pickupLocation.postalCode, pickupLocation.city].filter(Boolean).join(" ")}
          />
          <InfoRowNested
            label={t("expeditionDrawerDetails.phone")}
            value={pickupLocation.contactNumber}
          />
          {pickupLocation.contactNumber2 && (
            <InfoRowNested
              label={t("expeditionDrawerDetails.secondaryPhone")}
              value={pickupLocation.contactNumber2}
            />
          )}
          <InfoRowNested
            label={t("expeditionDrawerDetails.openingHours")}
            value={formatOpeningHoursDisplay(openingHours) || null}
          />
        </>
      ) : pickupInfo && hasOtherPickupInfo ? (
        <>
          {(pickupInfo.pickupCompany || otherFullName) && (
            <InfoRowNested
              label={t("expeditionDrawerDetails.name")}
              value={[pickupInfo.pickupCompany, otherFullName].filter(Boolean).join(" — ")}
            />
          )}
          {pickupInfo.pickupStreet && (
            <InfoRowNested
              label={t("expeditionDrawerDetails.address")}
              value={pickupInfo.pickupStreet}
            />
          )}
          {(pickupInfo.pickupPostalCode || pickupInfo.pickupCity) && (
            <InfoRowNested
              label={t("expeditionDrawerDetails.city")}
              value={[pickupInfo.pickupPostalCode, pickupInfo.pickupCity].filter(Boolean).join(" ")}
            />
          )}
          {pickupInfo.pickupCountry && (
            <InfoRowNested
              label={t("expeditionDrawerDetails.country")}
              value={pickupInfo.pickupCountry}
            />
          )}
          {pickupInfo.pickupPhone && (
            <InfoRowNested
              label={t("expeditionDrawerDetails.phone")}
              value={pickupInfo.pickupPhone}
            />
          )}
        </>
      ) : null}
    </InfoAccordionRow>
  );
};
